import React from 'react'
import PropTypes from 'prop-types'
import { Map, List } from 'immutable'
import compose from 'recompose/compose'
import _isNumber from 'lodash/isNumber'
import _parseInt from 'lodash/parseInt'
import { connect as connectRedux } from 'react-redux'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { selectTermById } from '../../services/node/index'
import { EN } from '../../services/languages/constants'
import { SCREEN_TYPE_CENTERS } from '../../services/page-manager/entities/index'
import { TOPIC_CLICKED_GA4_EVENT } from '../../services/event-tracking/index'
import { connect as connectStaticText } from '../StaticText/connect'
import { connect as connectPage } from '../Page/connect'
import { getUserLanguagePrimary } from '../../services/user/index'
import { H2, HEADING_TYPES } from '../Heading/index'
import IconV2, { ICON_TYPES } from '../Icon.v2/index'
import Jumbotron from '../JumbotronSubcategory/index'
import Link from '../Link/index'
import TopicsPageV3 from '../TopicsPageV3/index'

function noop () { }
const uiLocation = 'Topics'
const uiElement = 'Topic Link'

const yogaStaticTopics = new Map({
  en: [
    {
      to: '/yoga',
      label: 'Yoga Home',
    },
    {
      to: '/yoga/teachers',
      label: 'Yoga Teachers',
    },
    {
      to: 'https://www.gaia.com/articles/yoga-poses',
      label: 'Yoga Pose Library',
    },
  ],
  es: [
    {
      to: '/yoga',
      label: 'Yoga Home',
    },
    {
      to: 'https://www.gaia.com/es/articles/yoga-poses',
      label: 'Posturas de Yoga',
    },
  ],
  de: [],
  fr: [],
})

function YogaStaticLinks (props) {
  const { language } = props
  const staticTopics = yogaStaticTopics.get(language)

  if (staticTopics.length > 0) {
    return (
      <>
        {staticTopics.map((topic, key) => (
            <li key={`yoga-static-${key}`}>
              <Link
                to={topic.to}
                className="topics-v2__topic"
                ga4EventData={
                  TOPIC_CLICKED_GA4_EVENT
                    .mergeDeep({
                      topic_name: topic.label,
                      topic_id: 0,
                      ui_element: uiElement,
                      ui_location: uiLocation,
                    })
                }
              >
                {topic.label}
              </Link>
            </li>
        ))}
      </>
    )
  }

  return null
}

const Topic = (props) => {
  const { termData = Map() } = props
  // early return for empty term data
  if (!termData || !Map.isMap(termData)) return null
  const title = termData.get('title', null)
  const path = termData.get('path', null)
  const tid = termData.get('nid', null)
  const ga4TrackingData = TOPIC_CLICKED_GA4_EVENT
    .mergeDeep({
      topic_name: title,
      topic_id: tid,
      ui_element: uiElement,
      ui_location: uiLocation,
    })
  if (!path || !title) return null
  return (
    <li key={title}>
      <Link
        to={path}
        className="topics-v2__topic"
        ga4EventData={ga4TrackingData}
      >
        {title}
      </Link>
    </li>
  )
}

const TopicHOC = compose(
  connectRedux(
    (state, ownProps) => {
      const { contentId } = ownProps
      const language = state.user.getIn(['data', 'language', 0], 'en')
      const termData = selectTermById(state, { id: contentId, language, subPath: ['data'] })

      return {
        termData,
      }
    },
  ),
)(Topic)

const ParentTopic = (props) => {
  const { parentTermData, label } = props
  const path = parentTermData.get('path')
  const tid = parentTermData.get('nid')
  const ga4TrackingData = TOPIC_CLICKED_GA4_EVENT
    .mergeDeep({
      topic_name: label,
      topic_id: tid,
      ui_element: uiElement,
      ui_location: uiLocation,
    })

  if (!path) {
    return (label)
  }

  return (
    <Link
      to={path}
      className="topics-v2__topic-parent"
      ga4EventData={ga4TrackingData}
    >
      {label}
      <IconV2 className="topics-v2__category-chevron" type={ICON_TYPES.CHEVRON_RIGHT} />
    </Link>
  )
}

const TopicParentHOC = compose(
  connectRedux(
    (state, ownProps) => {
      const { parentTermId, label } = ownProps
      const language = state.user.getIn(['data', 'language', 0], 'en')
      const parentTermData = selectTermById(state, { id: parentTermId, language, subPath: ['data'] })

      return {
        parentTermData,
        label,
      }
    },
  ),
)(ParentTopic)

const TopicsSection = (props) => {
  const { sectionStore, sectionId, language } = props
  // this is the main (first) tab, and we get the label from here
  const data = sectionStore.getIn([sectionId, 'data', 'data', 'tabs', 0])
  const parentTermId = sectionStore.getIn([sectionId, 'data', 'data', 'footer', 'label'])
    ? _parseInt(sectionStore.getIn([sectionId, 'data', 'data', 'footer', 'label'], undefined), 10) : undefined
  const headerLabel = sectionStore.getIn([sectionId, 'data', 'data', 'header', 'label'])
  const yogaHeaderLabelRegex = /^yoga$/i
  const shouldRenderYogaLinks = headerLabel && yogaHeaderLabelRegex.test(headerLabel)

  if (data) {
    const label = data.get('tabLabel')

    // allow multiple lists, and merge the data into 1 list
    const tabContent = (sectionStore.getIn([sectionId, 'data', 'data', 'tabs']) || List()).flatMap((t) => t.get('tabContent') || List())

    return (
      <div className="topics-v2__category-wrapper">
        <H2 as={HEADING_TYPES.H4} className="topics-v2__category">
          {
            <TopicParentHOC
              parentTermId={_isNumber(parentTermId) ? parentTermId : undefined}
              key={parentTermId}
              label={label}
            />
          }
        </H2>
        <ul className="topics-v2__category-container">
          {
            shouldRenderYogaLinks
              ? <YogaStaticLinks language={language} />
              : null
          }
          {tabContent.map((content) => (<TopicHOC contentId={content.get('contentId')} key={`${parentTermId}-${content.get('contentId')}`} />))}
        </ul>
      </div>
    )
  }
  return null
}

const TopicsPageV2 = (props) => {
  const {
    staticText,
    auth,
    sectionIds,
    sectionStore,
    language,
  } = props

  if (language === EN) {
    return (
      <TopicsPageV3 />
    )
  }
  return (
    <div className="topics-v2">
      <Jumbotron
        auth={auth}
        description={staticText.getIn(['data', 'subTitle'])}
        title={staticText.getIn(['data', 'topics'])}
        setOverlayDialogVisible={noop}
        staticText={Map()}
        heroImage={Map()}
      />
      <div className="topics-v2__wrapper">
        {sectionIds.map((sectionId) => (
          <TopicsSection
            sectionId={sectionId}
            sectionStore={sectionStore}
            key={sectionId}
            language={language}
          />
        ))}
      </div>
    </div>
  )
}

TopicsPageV2.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
}

export default compose(
  connectPage({ storeBranch: 'topics' }),
  connectRedux(
    (state) => {
      const {
        pmSection,
        pmScreen,
        user,
        staticText,
      } = state
      const language = getUserLanguagePrimary(user) || EN
      const screenStore = pmScreen.getIn([SCREEN_TYPE_CENTERS, language]) || Map()
      const sectionIds = screenStore.getIn(['data', 'sectionIds']) || List()
      return {
        staticText,
        sectionStore: pmSection,
        language,
        sectionIds,
      }
    },
  ),
  connectStaticText({ storeKey: 'topics' }),
)(TopicsPageV2)
